/*
 * TUT-296a — o cliente FININHO do chat próprio: só os métodos de API. A
 * TELA (o ícone, o painel, as bolhas) é a TUT-296b — aqui não há nenhum
 * componente React, só a porta que ela vai chamar.
 *
 * Módulo AUTOCONTIDO de propósito: `api.ts` guarda `csrfToken` como estado
 * de módulo privado (preenchido no login) e não o exporta, então em vez de
 * inchar aquele arquivo compartilhado por dezenas de domínios, este lê o
 * cookie `tutelae_csrf` direto — ele é de leitura por design (dupla
 * submissão: cookie + cabeçalho precisam bater, `security.constants.ts`),
 * então não há segredo exposto por isso.
 */

import { criarClienteDeChatDemo } from "./chat-demo";

const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

/**
 * TUT-545 — `ESCRITORIO`: a conversa fixa de quem é de fora (advogado,
 * assistente externo) com o escritório, uma por pessoa, que quem está no
 * Operacional atende (e os Administradores, com o Operacional vazio).
 */
export type ChatConversationKind = "DIRECT" | "GROUP" | "ESCRITORIO";

export interface ChatParticipant {
  userId: string;
  name: string;
  role: string;
}

export interface ChatConversation {
  id: string;
  kind: ChatConversationKind;
  title: string | null;
  /** TUT-545 — o externo dono da conversa `ESCRITORIO`; `null` nas outras. */
  externalUserId: string | null;
  /**
   * TUT-545 — a direta ou o grupo de antes, com alguém de fora: fica para
   * consulta, e postar dá 409 `CHAT_PORTAL_CONVERSA_ENCERRADA`.
   */
  readOnly: boolean;
  /** No portal, só o próprio externo: ele não vê a equipe. */
  participants: ChatParticipant[];
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string | null;
}

export interface ChatConversationSummary extends ChatConversation {
  lastMessagePreview: string | null;
  unreadCount: number;
  /** TUT-491 — alguém me mencionou aqui (põe o grupo em "Minhas"). */
  mentionedMe?: boolean;
  /** TUT-491 — há menção a mim que eu ainda não li (o selo "Mencionou você"). */
  unreadMentionMe?: boolean;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  authorUserId: string;
  authorName: string;
  body: string;
  /** TUT-491 — quem foi chamado com `@` nesta mensagem. */
  mentionedUserIds?: string[];
  createdAt: string;
}

export interface ChatDirectoryEntry {
  userId: string;
  name: string;
  role: string;
}

/**
 * TUT-545 — os códigos que a tela trata: `CHAT_PORTAL_ONLY_OFFICE` (403: no
 * portal, só a conversa com o escritório), `CHAT_EXTERNAL_ONLY_OFFICE` (409:
 * direta ou grupo com alguém de fora) e `CHAT_PORTAL_CONVERSA_ENCERRADA` (409:
 * a conversa antiga com alguém de fora é só leitura).
 */
class ChatApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
  }
}

function csrfTokenDoCookie(): string {
  const par = document.cookie
    .split("; ")
    .find((linha) => linha.startsWith("tutelae_csrf="));
  return par ? decodeURIComponent(par.split("=")[1] ?? "") : "";
}

/** `/api` ou `/portal/api` — a mesma porta, o mesmo cliente. */
async function chamar<T>(
  prefixo: "" | "/portal",
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body) headers.set("Content-Type", "application/json");
  if (init.method && init.method !== "GET") {
    headers.set("X-CSRF-Token", csrfTokenDoCookie());
  }
  const response = await fetch(`/api${prefixo}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      message?: string;
      code?: string;
    } | null;
    throw new ChatApiError(
      body?.message ?? "Não foi possível concluir a solicitação do chat.",
      response.status,
      body?.code,
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

/** `portal: true` chama `/portal/chat/...` — mesmas respostas, mesmas regras. */
export function criarClienteDeChat(portal: boolean) {
  // `tools/run-e2e.mjs` compila em VITE_API_MODE=demo e serve sem API atrás
  // (vite preview) — sem este desvio, toda tela de chat morreria em 404 na
  // e2e. Mesmo padrão de `useDemoApi` em `api.ts`.
  if (useDemoApi) return criarClienteDeChatDemo(portal);
  const prefixo = portal ? "/portal" : "";
  return {
    /**
     * TUT-545 — a conversa fixa de quem é de fora com o escritório (só no
     * portal). Idempotente: cria na primeira vez e devolve sempre a mesma.
     */
    escritorio: () => chamar<ChatConversation>(prefixo, "/chat/escritorio", { method: "POST" }),
    listConversations: () =>
      chamar<{ items: ChatConversationSummary[] }>(prefixo, "/chat/conversations"),
    createDirect: (otherUserId: string) =>
      chamar<ChatConversation>(prefixo, "/chat/conversations/direct", {
        method: "POST",
        body: JSON.stringify({ otherUserId }),
      }),
    createGroup: (title: string, userIds: string[]) =>
      chamar<ChatConversation>(prefixo, "/chat/conversations/group", {
        method: "POST",
        body: JSON.stringify({ title, userIds }),
      }),
    listMessages: (conversationId: string, page = 1, pageSize = 50) =>
      chamar<{ items: ChatMessage[]; total: number }>(
        prefixo,
        `/chat/conversations/${conversationId}/messages?page=${page}&pageSize=${pageSize}`,
      ),
    postMessage: (conversationId: string, body: string, mentionUserIds: string[] = []) =>
      chamar<ChatMessage>(prefixo, `/chat/conversations/${conversationId}/messages`, {
        method: "POST",
        body: JSON.stringify(mentionUserIds.length ? { body, mentionUserIds } : { body }),
      }),
    markRead: (conversationId: string) =>
      chamar<{ ok: boolean }>(prefixo, `/chat/conversations/${conversationId}/read`, {
        method: "POST",
      }),
    directory: () =>
      chamar<{ items: ChatDirectoryEntry[] }>(prefixo, "/chat/directory"),
  };
}

export { ChatApiError };
